import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  Autocomplete,
  TextField,
} from "@mui/material";
import {
  Close as CloseIcon,
  Link as LinkIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import axiosInstance from "../utlis/axiosInstance";
import { useDevicesApi } from "../utlis/useDevicesApi";

interface ConnectionsFormProps {
  id: string | null;
  open: boolean;
  onClose: () => void;
  type?: string;
}

interface DropdownItem {
  label: string;
  value: string;
}

interface ExistingConnection {
  configuration_id: number;
  user: number | null;
  device: number | null;
  group: number;
  geofence: number | null;
  notification: number | null;
  calendar: number | null;
  attribute: number | null;
  driver: number | null;
  managed_user: number | null;
  command: number | null;
}

const ConnectionsForm: React.FC<ConnectionsFormProps> = ({
  id,
  open,
  onClose,
}) => {
  const { devices } = useDevicesApi();
  const [allNotifications, setAllNotifications] = useState<DropdownItem[]>([]);
  const [users, setUsers] = useState<DropdownItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>("");

  // State for existing connections
  const [existingConnections, setExistingConnections] = useState<
    ExistingConnection[]
  >([]);
  const [selectedDevices, setSelectedDevices] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [selectedNotifications, setSelectedNotifications] = useState<any[]>([]);

  const showSuccessMessage = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => {
      setSuccessMessage("");
    }, 3000); // Hide after 3 seconds
  };

  // Fetch user details by ID
  const fetchUserDetails = async (userId: number): Promise<string> => {
    try {
      const response = await axiosInstance.get(
        `/gpstracker/userhandler/${userId}`,
      );
      if (response.data && response.data.responseData) {
        const user = response.data.responseData;
        return user.name || user.email || `User ${userId}`;
      }
    } catch (error) {
      console.error(`Error fetching user ${userId}:`, error);
    }
    return `User ${userId}`;
  };

  // Fetch existing connections for the group
  const fetchExistingConnections = async () => {
    if (!id) return;

    try {
      const response = await axiosInstance.get(
        `/gpstracker/configurationhandler/?limit=1000&offset=0&group_id=${id}`,
      );
      if (
        response.data &&
        response.data.responseData &&
        Array.isArray(response.data.responseData)
      ) {
        const rawConnections = response.data.responseData;
        // Log first item so we can see the actual field names from the API
        if (rawConnections.length > 0) {
          console.log(
            "[ConnectionsForm] Raw connection object keys:",
            Object.keys(rawConnections[0]),
          );
          console.log(
            "[ConnectionsForm] First raw connection:",
            rawConnections[0],
          );
        }
        setExistingConnections(rawConnections);

        const devices: any[] = [];
        const selectedUsersList: any[] = [];
        const notifications: any[] = [];

        // Process connections and fetch user details if needed
        for (const connection of rawConnections) {
          // Handle devices — store the full connection so we can delete by its record ID
          if (connection.device) {
            const device = devices.find((d) => d.id === connection.device);
            if (device) {
              devices.push({
                id: connection.device,
                name: device.name,
                connectionRecord: connection,
              });
            } else {
              devices.push({
                id: connection.device,
                name: `Device ${connection.device}`,
                connectionRecord: connection,
              });
            }
          }

          // Handle users
          if (connection.user) {
            const user = users.find(
              (u: DropdownItem) => u.value === connection.user,
            );
            if (user) {
              selectedUsersList.push({
                id: connection.user,
                name: user.label,
                connectionRecord: connection,
              });
            } else {
              const userName = await fetchUserDetails(connection.user);
              selectedUsersList.push({
                id: connection.user,
                name: userName,
                connectionRecord: connection,
              });
            }
          }

          // Handle notifications
          if (connection.notification) {
            const notification = allNotifications.find(
              (n) => n.value === connection.notification,
            );
            if (notification) {
              notifications.push({
                id: connection.notification,
                name: notification.label,
                connectionRecord: connection,
              });
            } else {
              notifications.push({
                id: connection.notification,
                name: `Notification ${connection.notification}`,
                connectionRecord: connection,
              });
            }
          }
        }

        setSelectedDevices(devices);
        setSelectedUsers(selectedUsersList);
        setSelectedNotifications(notifications);
      }
    } catch (error) {
      console.error("Error fetching existing connections:", error);
    }
  };

  const addDevice = async (deviceId: string) => {
    try {
      const response = await axiosInstance.post(
        "/gpstracker/configurationhandler/",
        {
          user_id: null,
          device_id: parseInt(deviceId),
          group_id: parseInt(id || "0"),
          geofence_id: null,
          notification_id: null,
          calendar_id: null,
          attribute_id: null,
          driver_id: null,
          managed_user_id: null,
          command_id: null,
        },
      );

      if (response.data) {
        showSuccessMessage("Device added successfully!");
        await fetchExistingConnections();
      }
    } catch (error) {
      console.error("Error adding device:", error);
    }
  };

  const addUser = async (userId: string) => {
    try {
      const response = await axiosInstance.post(
        "/gpstracker/configurationhandler/",
        {
          user_id: parseInt(userId),
          device_id: null,
          group_id: parseInt(id || "0"),
          geofence_id: null,
          notification_id: null,
          calendar_id: null,
          attribute_id: null,
          driver_id: null,
          managed_user_id: null,
          command_id: null,
        },
      );

      if (response.data) {
        showSuccessMessage("User added successfully!");
        await fetchExistingConnections();
      }
    } catch (error) {
      console.error("Error adding user:", error);
    }
  };

  const addNotification = async (notificationId: string) => {
    try {
      await axiosInstance.post("/gpstracker/configurationhandler/", {
        user_id: null,
        device_id: null,
        group_id: parseInt(id || "0"),
        geofence_id: null,
        notification_id: parseInt(notificationId),
        calendar_id: null,
        attribute_id: null,
        driver_id: null,
        managed_user_id: null,
        command_id: null,
      });
      showSuccessMessage("Notification added successfully!");
      await fetchExistingConnections();
    } catch (error) {
      console.error("Error adding notification:", error);
    }
  };

  // Configuration handler functions for removing permissions
  const deleteDevice = async (item: any) => {
    try {
      const rec = item.connectionRecord;
      const connId = rec?.configuration_id ?? rec?.id;
      console.log("[deleteDevice] connectionRecord:", rec, "connId:", connId);
      if (connId) {
        await axiosInstance.delete(
          `/gpstracker/configurationhandler/${connId}`,
        );
        showSuccessMessage("Device removed successfully!");
        await fetchExistingConnections();
      } else {
        console.error(
          "[deleteDevice] Could not find connection record ID. Full item:",
          item,
        );
      }
    } catch (error) {
      console.error("Error removing device:", error);
    }
  };

  const deleteUser = async (item: any) => {
    try {
      const rec = item.connectionRecord;
      const connId = rec?.configuration_id ?? rec?.id;
      console.log("[deleteUser] connectionRecord:", rec, "connId:", connId);
      if (connId) {
        await axiosInstance.delete(
          `/gpstracker/configurationhandler/${connId}`,
        );
        showSuccessMessage("User removed successfully!");
        await fetchExistingConnections();
      } else {
        console.error(
          "[deleteUser] Could not find connection record ID. Full item:",
          item,
        );
      }
    } catch (error) {
      console.error("Error removing user:", error);
    }
  };

  const deleteNotification = async (item: any) => {
    try {
      const rec = item.connectionRecord;
      const connId = rec?.configuration_id ?? rec?.id;
      console.log(
        "[deleteNotification] connectionRecord:",
        rec,
        "connId:",
        connId,
      );
      if (connId) {
        await axiosInstance.delete(
          `/gpstracker/configurationhandler/${connId}`,
        );
        showSuccessMessage("Notification removed successfully!");
        await fetchExistingConnections();
      } else {
        console.error(
          "[deleteNotification] Could not find connection record ID. Full item:",
          item,
        );
      }
    } catch (error) {
      console.error("Error removing notification:", error);
    }
  };

  const getAllNotifications = async () => {
    try {
      const response = await axiosInstance.get(
        `/gpstracker/notificationhandler/?limit=1000&offset=0`,
      );
      if (response.data && response.data.responseData) {
        setAllNotifications(
          response.data.responseData.map((item: any) => ({
            label: item.notification_type,
            value: item.id,
          })),
        );
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
      setAllNotifications([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get("/gpstracker/userhandler/");
      if (response.data && response.data.responseData) {
        setUsers(
          response.data.responseData.map((item: any) => ({
            label: item.name,
            value: item.id,
          })),
        );
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    }
  };

  useEffect(() => {
    if (id && open) {
      const loadAllData = async () => {
        setIsLoading(true);
        try {
          await Promise.all([getAllNotifications(), fetchUsers()]);
        } catch (error) {
          console.error("Error loading data:", error);
        } finally {
          setIsLoading(false);
          await fetchExistingConnections();
        }
      };

      loadAllData();
    }
  }, [id, open]);

  // Update names when dropdown data is loaded
  useEffect(() => {
    if (existingConnections.length > 0) {
      // Update device names
      if (devices.length > 0) {
        const updatedDevices = selectedDevices.map((selectedDevice) => {
          const device = devices.find((d) => d.id === selectedDevice.id);
          if (device) {
            return {
              ...selectedDevice,
              name: device.name,
            };
          }
          return selectedDevice;
        });

        if (
          JSON.stringify(updatedDevices) !== JSON.stringify(selectedDevices)
        ) {
          setSelectedDevices(updatedDevices);
        }
      }

      // Update user names
      if (users.length > 0) {
        const updatedUsers = selectedUsers.map((selectedUser) => {
          const user = users.find(
            (u: DropdownItem) => u.value === selectedUser.id.toString(),
          );
          if (user) {
            return {
              ...selectedUser,
              name: user.label,
            };
          }
          return selectedUser;
        });

        if (JSON.stringify(updatedUsers) !== JSON.stringify(selectedUsers)) {
          setSelectedUsers(updatedUsers);
        }
      }

      // Update notification names
      if (allNotifications.length > 0) {
        const updatedNotifications = selectedNotifications.map(
          (selectedNotification) => {
            const notification = allNotifications.find(
              (n) => n.value === selectedNotification.id,
            );
            if (notification) {
              return {
                ...selectedNotification,
                name: notification.label,
              };
            }
            return selectedNotification;
          },
        );

        if (
          JSON.stringify(updatedNotifications) !==
          JSON.stringify(selectedNotifications)
        ) {
          setSelectedNotifications(updatedNotifications);
        }
      }
    }
  }, [devices, users, allNotifications, existingConnections]);

  const renderSection = (
    title: string,
    items: any[],
    onDelete: (item: any) => void,
    onAdd: (id: string) => void,
    allItems: DropdownItem[],
  ) => {
    const safeItems = Array.isArray(items) ? items : [];
    const safeAllItems = Array.isArray(allItems) ? allItems : [];

    return (
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{ mb: 2, color: "#1565C0", fontWeight: 600 }}
        >
          {title}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {safeItems.map((item) => (
            <Chip
              key={item.id}
              label={item.name}
              onDelete={() => onDelete(item)}
              deleteIcon={<DeleteIcon />}
              sx={{
                backgroundColor: "#e3f2fd",
                color: "#1565C0",
                "& .MuiChip-deleteIcon": {
                  color: "#FF3D00",
                },
              }}
            />
          ))}
        </Box>
        <Autocomplete
          options={safeAllItems}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          renderInput={(params) => (
            <TextField
              {...params}
              label={`Add ${title}`}
              size="small"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    {params.InputProps.startAdornment}
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                  </>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#e3f2fd",
                  "&:hover": { bgcolor: "#bbdefb" },
                  "&.Mui-focused": { bgcolor: "white" },
                },
              }}
            />
          )}
          onChange={(event, newValue) => {
            if (newValue && newValue.value) {
              onAdd(newValue.value);
            }
          }}
          loading={isLoading}
          clearOnBlur
          clearOnEscape
          selectOnFocus
        />
      </Box>
    );
  };

  // Convert devices from API to DropdownItem format for the devices section
  const deviceOptions = devices
    .filter((device: any) => !device.inventory)
    .map((device: any) => ({
      label: device.name,
      value: device.id,
    }));

  if (!open || !id) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: "white",
          border: "1px solid",
          borderColor: "#e2e8f0",
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <LinkIcon sx={{ mr: 1, color: "#1565C0" }} />
            <Typography variant="h5" fontWeight={600} color="#1565C0">
              Group Connections
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "#1565C0" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            {successMessage}
          </Alert>
        )}
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 8,
            }}
          >
            <CircularProgress sx={{ color: "#1565C0" }} />
            <Typography variant="body1" sx={{ ml: 2, color: "#1565C0" }}>
              Loading connections...
            </Typography>
          </Box>
        ) : (
          <>
            {renderSection(
              "Notifications",
              selectedNotifications,
              deleteNotification,
              addNotification,
              allNotifications,
            )}
            <Divider sx={{ my: 2 }} />
            {renderSection("Users", selectedUsers, deleteUser, addUser, users)}
            <Divider sx={{ my: 2 }} />
            {renderSection(
              "Devices",
              selectedDevices,
              deleteDevice,
              addDevice,
              deviceOptions,
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConnectionsForm;
